<template>
  <div
    class="header-video-editorial"
    :class="{'on-mobile': $device.isMobile }"
    v-intersect @intersect-enter="videoIn"
    @intersect-leave="videoOut"
    role="region"
    aria-label="Video Player"
  >
    <div
      class="video-container"
      :aria-hidden="!hideImage"
      :class="{'hide': !hideImage }"
      >
      <youtube
        class="video"
        ref="youtube"
        :video-id="videoId"
        :player-vars="playerVars"
        @ready="ready"
        @paused="playing(false)"
        @ended="playing(false)"
        @playing="playing(true)">
      </youtube>
    </div>
    <div class="image-container" @click="videoControl" v-bind:class="{'hide-image': hideImage }">
      <custom-image v-if="showCustomPreview" class="image" :media="fields.customImage" ref="videoImage" size="cloudinary_header_video_image_large"/>
      <img v-else :src="youtubePreviewSrc" :alt="youtubePreviewAlt" class="image" ref="videoImage">
      <div class="mask" role="img" :aria-label="fields.hiddenVideoTitle.value"></div>
      <button class="play-button" :aria-label="$t('MORE_CONTENT_PLAY_CTA')">
        <icon class="play-icon" icon="play-video" size="large"/>
        <label class="play-label">{{ $t('MORE_CONTENT_PLAY_CTA') }}</label>
      </button>
    </div>
  </div>
</template>

<script>
import { Text } from '@sitecore-jss/sitecore-jss-vue'
import { createNamespacedHelpers } from 'vuex'
import CustomImage from '../custom-image'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')

export default {
  name: 'HeaderVideoEditorial',
  props: {
    fields: {
      type: Object
    },
    params: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      videoId: '',
      isMobile: false,
      isVideoAutoplay: false,
      isClickPlayback: false,
      isPlaybackStarted: false,
      isMuted: false,
      isPlaying: false,
      isPaused: false,
      isHolded: false,
      playerVars: {
        autoplay: 0,
        controls: 1,
        start: 0,
        loop: 0
      }
    }
  },
  components: {
    CustomImage,
    ScText: Text
  },
  created () {
    //  Get ID form href
    const hrefValue = this.fields.mainVideo ? this.fields.mainVideo.value : ''
    this.videoId = this.getId(hrefValue)
    // Set start time
    if (this.fields.startTime !== undefined && this.fields.startTime.value !== 0) {
      this.playerVars.start = this.fields.startTime.value
    }
    // Set Loop param
    if (this.params.loop !== undefined && this.params.loop === '1') {
      this.playerVars.loop = 1
      this.playerVars.playlist = this.videoId
    }
  },
  computed: {
    ...mapJssGetters(['isEditMode']),

    youtubePreviewSrc () {
      // render preview image, if no custom image
      return `https://img.youtube.com/vi/${this.videoId}/maxresdefault.jpg`
    },
    youtubePreviewAlt () {
      if (this.fields.title !== undefined) {
        return this.fields.title.value
      }
      return this.$t('VIDEO_PREVIEW_YOUTUBE_ALT')
    },
    showCustomPreview () {
      return this.fields.customImage.value && this.fields.customImage.value.src && !this.isEditMode
    },
    isReadToPlay () {
      return !this.isEditMode && this.params.playSettings === 'auto-play' && !this.$device.isMobile
    },
    player () {
      return this.$refs.youtube.player
    },
    hideImage () {
      return this.isPlaybackStarted || this.isVideoAutoplay
    }
  },
  methods: {
    getId (url) {
      // const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|v=)([^#]*).*/
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
      const match = url.match(regExp)

      if (match && match[2].length === 11) {
        const [,, result] = match
        return result
      }

      return 'error'
    },
    ready () {
      // set origin for Youtube video
      this.playerVars.origin = window.location.origin
      // Get Autoplay param
      if (this.isReadToPlay) {
        this.isVideoAutoplay = true
        this.player.playVideo()
      }
      // Get Mute param
      if (this.params.muted && this.params.muted === '1') {
        this.isMuted = true
        this.player.mute()
      }
    },
    videoControl () {
      if (!this.isVideoAutoplay && !this.isEditMode) {
        if (!this.isClickPlayback) {
          this.playVideo()
        } else {
          this.pauseVideo()
        }
      }
    },
    playVideo () {
      if (!this.isPlaybackStarted) {
        this.isPlaybackStarted = true
      }
      this.isClickPlayback = true
      this.player.playVideo()
    },
    pauseVideo () {
      this.isClickPlayback = false
      this.player.pauseVideo()
    },
    playing (val) {
      this.isPlaying = val
    },
    videoIn () {
      if (this.isHolded) {
        this.player.playVideo()
        this.isHolded = false
      }
    },
    videoOut () {
      if (this.isPlaying && this.$device.isMobile) {
        this.player.pauseVideo()
        this.isHolded = true
      }
    }

  }
}
</script>

<style lang="scss" scoped>

.header-video-editorial {
  @include ratio(16, 9);

  position: relative;
  width: 100%;
  height: auto;
  background: $color-black;

  @include tablet {
    height: 650px;
  }

  &.body-editorial-video {
    @include tablet {
      height: auto;
    }
  }

  &.on-mobile {
    height: auto;
    @include ratio(16, 9);
  }

  .image-container {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;

    &.hide-image {
      display: none;
    }

    .image {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      @include lazy-load;
    }

    .mask {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.62);
    }

    .play-button {
      position: absolute;
      top: 50%;
      transform: translateY(-50%) translateX(-50%);
      left: 50%;
      text-align: center;
      cursor: pointer;
      border: 0;
      background: transparent;

      &:focus {
        outline: 1px solid $color-white;
      }

      .play-icon {
        display: block;

        .large {
          width: 60px;
          height: 60px;
        }
      }

      .play-label {
        @include font('090');
        font-stretch: normal;
        letter-spacing: normal;
        font-style: italic;
        color: $color-white;
        margin: $space-050 0 $d-xx-large;
        font-weight: 600;
      }
    }
  }

  .video-container {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;

    ::v-deep .video {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;
    }

    &.hide {
      display: none;
    }
  }

}
</style>
